import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';

import Modal from '@/components/atoms/Modal';
import Button from '@/components/buttons/Button';
import Turnstile, {
  isTurnstileEnabled,
  type TurnstileHandle,
} from '@/components/Turnstile';

import { PUBLIC_IMAGE_REPORT_TURNSTILE_ACTION } from '@/constants/turnstile';

interface PublicImageReportModalProps {
  imageSlug: string;
  showModal: boolean;
  onClose: () => void;
  onTakenDown?: () => void;
}

interface ReportResult {
  takenDown: boolean;
  message: string;
}

const MAX_REASON_LENGTH = 1000;

export default function PublicImageReportModal({
  imageSlug,
  showModal,
  onClose,
  onTakenDown,
}: PublicImageReportModalProps) {
  const { t } = useTranslation('ai-images');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [error, setError] = useState('');
  const turnstileRef = useRef<TurnstileHandle>(null);

  useEffect(() => {
    if (!showModal) return;
    setReason('');
    setResult(null);
    setError('');
  }, [imageSlug, showModal]);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      const turnstileToken = await turnstileRef.current?.getToken();
      if (isTurnstileEnabled && !turnstileToken) {
        setError(t('Please complete the security check and try again.'));
        return;
      }

      const response = await fetch('/api/report-public-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slug: imageSlug,
          reason: reason.trim() || undefined,
          turnstileToken,
        }),
      });
      const data = (await response.json()) as ReportResult & { error?: string };

      if (!response.ok) {
        throw new Error(data.error || t('Unable to submit report'));
      }

      setResult(data);
      if (data.takenDown) onTakenDown?.();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t('Unable to submit report'),
      );
    } finally {
      // Turnstile tokens expire and are single-use, including failed requests.
      turnstileRef.current?.reset();
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      showModal={showModal}
      onClose={handleClose}
      className='sm:max-w-lg'
      doNotCloseOnOverlayClick={isSubmitting}
    >
      <div className='p-6 sm:p-8'>
        <h2 className='pr-8 text-xl font-semibold text-gray-900 dark:text-slate-100'>
          {result ? t('Report received') : t('Report this image')}
        </h2>

        {result ? (
          <div className='mt-5'>
            <p className='text-sm leading-6 text-gray-600 dark:text-slate-300'>
              {result.takenDown
                ? t(
                    'Thank you for your report. The image has been removed from the public gallery.',
                  )
                : t(
                    'Thank you for your report. Our support team has received it for review.',
                  )}
            </p>
            <div className='mt-6 flex justify-end'>
              <Button variant='primary' onClick={handleClose}>
                {t('Close')}
              </Button>
            </div>
          </div>
        ) : (
          <form className='mt-5' onSubmit={handleSubmit}>
            <label
              htmlFor='public-image-report-reason'
              className='block text-sm font-medium text-gray-700 dark:text-slate-200'
            >
              {t("What's wrong with this image?")}{' '}
              <span className='font-normal text-gray-500 dark:text-slate-400'>
                {t('(optional)')}
              </span>
            </label>
            <textarea
              id='public-image-report-reason'
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={MAX_REASON_LENGTH}
              rows={5}
              disabled={isSubmitting}
              placeholder={t(
                'Tell us what appears inappropriate or unsafe in this image.',
              )}
              className='mt-2 block w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-70 dark:border-gray-700 dark:bg-slate-800 dark:text-slate-100'
            />
            <div className='mt-1 text-right text-xs text-gray-400'>
              {reason.length}/{MAX_REASON_LENGTH}
            </div>

            <Turnstile
              ref={turnstileRef}
              action={PUBLIC_IMAGE_REPORT_TURNSTILE_ACTION}
              context='report'
              className='mt-5 flex justify-center'
            />

            {error ? (
              <p
                role='alert'
                className='mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300'
              >
                {error}
              </p>
            ) : null}

            <div className='mt-6 flex justify-end gap-3'>
              <Button
                variant='light'
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('Cancel')}
              </Button>
              <Button type='submit' isLoading={isSubmitting}>
                {isSubmitting ? t('Reviewing report...') : t('Send report')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
